import {
    CambrianImageProcessor,
    DirectImageUploader,
    getConfig,
    GetUploadUrlsRequest,
    ImageUploader,
    PresignedUrlImageUploader
} from "./CBServerApi";
import {CBSceneProperties} from "..";

let contentManager: CBContentManager|undefined;

export class CBContentManager {

    public roomId:string|undefined;
    private roomUploadUrl:string|undefined;
    public subroomId:string|undefined;
    private _tempSubroomId:string|undefined;
    private roomSubUploadUrls:{[name: string]: string}|undefined;

    private readonly imageUploader:ImageUploader;
    private readonly imageProcessor:CambrianImageProcessor;

    public static get default(): CBContentManager {
        if (!contentManager) {
            contentManager = new CBContentManager()
        }
        return contentManager
    }

    constructor(private config = getConfig()) {
        this.imageUploader = config.uploadUrl !== config.projectHostingUrl ? new PresignedUrlImageUploader(config.uploadUrl)
            : new DirectImageUploader(config.uploadUrl);

        this.imageProcessor = new CambrianImageProcessor(config.processingUrl)
    }

    private async initiateUploads() {
        const request: GetUploadUrlsRequest = {
            names: this.config.uploadNames
        };
        if (this.roomId) {
            request.key = this.roomId
        }

        const response = await this.imageUploader.getUploadUrls(request).catch(() => {
            console.warn("Image uploader unavailable.");
            return
        });

        if (response) {
            if (!this.roomId) {
                this.roomId = response.key;
                this.roomUploadUrl = response.uploadUrl || undefined;
                this._tempSubroomId = response.subKey;
                console.log(`Room ID now ${this.roomId}`)
            } else {
                console.log(`Subroom ID now ${this.roomId}`)
                this.subroomId = response.subKey;
            }

            this.roomSubUploadUrls = response.subUploadUrls;
        } else {
            console.warn("Image uploader returned invalid data.")
        }
    }

    public get scene():CBSceneProperties|undefined {
        if (this.roomId) {
            const config = getConfig();
            const roomUrls = this.getRoomUrls(config.projectHostingUrl, this.roomId, ["background", "mask", "lighting", "superpixels"]);
            const backgroundUrl = roomUrls["background"];
            const lightingUrl = roomUrls["lighting"];
            const superpixelsUrl = roomUrls["superpixels"];
            let maskUrl = roomUrls["mask"];

            if (this.subroomId) {
                const subUrls = this.getRoomSubUrls(config.projectHostingUrl, this.roomId, this.subroomId, ["mask"]);
                maskUrl = subUrls["mask"]
            }

            return {
                backgroundUrl: backgroundUrl,
                lightingUrl: lightingUrl,
                superpixelsUrl: superpixelsUrl,
                masks:{
                    "floor": maskUrl
                }
            }
        }
        return undefined
    }

    public resetScene() {
        this.roomId = undefined;
        this.subroomId = undefined;
        this.roomSubUploadUrls = undefined
    }


    public synchronize(searchObject:any) {
        const roomId = searchObject.room as string;
        if (roomId && roomId != this.roomId) {
            this.roomId = roomId
        }

        const subroomId = searchObject.subroom as string;
        if (subroomId && subroomId != this.subroomId) {
            this.subroomId = subroomId
        }

        if (this.roomId) {
            searchObject.room = this.roomId;
        }

        if (this.subroomId) {
            searchObject.subroom = this.subroomId;
        }
    }

    public async uploadRoom(image:File | Blob, onProgress?:(progress:number, status:string)=>void) {

        if (onProgress) onProgress(0.1, "Contacting Server");
        if (!this.roomUploadUrl || !this.roomId) {
            await this.initiateUploads();
            if (!this.roomUploadUrl || !this.roomId) {
                console.error("No upload URL available, cannot proceed");
                return null
            }
        }

        // Upload the dropped image
        if (onProgress) onProgress(0.3, "Uploading Image");

        const success = await this.imageUploader.upload(this.roomUploadUrl, image);

        if (!success) {
            console.error("imageUploader Failed");
            return null
        }

        if (onProgress) onProgress(0.75, "Running Algorithms");

        const results = await this.imageProcessor.processImage(this.roomId);

        if (onProgress) onProgress(1.0, "Complete");

        return results
    }

    public async uploadFile(canvas: HTMLCanvasElement, name: string) {

        console.log(`Uploading ${name}`);

        if (!this.roomSubUploadUrls || (this.config.uploadNames.indexOf(name) >= 0 && !this.roomSubUploadUrls[name])) {
            //not initiated OR already used, new series, re-initiate
            await this.initiateUploads();

            if (!this.roomSubUploadUrls) {
                console.error("Tried to upload project but upload urls were null");
                return null
            }
        }

        if (!this.subroomId) {
            if (this._tempSubroomId) {
                this.subroomId = this._tempSubroomId;
            } else {
                await this.initiateUploads();

                if (!this.subroomId) {
                    console.error("No subroom, cannot upload");
                    return null;
                }
            }
        }

        const imageUploadUrl = this.roomSubUploadUrls[name];
        if (!imageUploadUrl) {
            console.error(`Upload of ${name} is an unknown name. Was it passed during initialization?`);
            return null
        }

        try {
            // Try to convert the edited mask canvas to a blob
            const maskBlobPromise = new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(blob => {
                    if (blob) {
                        resolve(blob)
                    } else {
                        reject()
                    }
                })
            });

            const [maskBlob] = await Promise.all([maskBlobPromise]);

            // Upload mask and preview blobs (or only mask if we couldn't get the preview)
            const maskKeyPromise = this.imageUploader.upload(imageUploadUrl, maskBlob);

            if (!maskKeyPromise) {
                console.error(`Upload of ${name} FAILED`);
                return null
            }

            //e.g. https://s3.amazonaws.com/roomvisuserupload/mAy0yrkBup/ramxkkx1/preview
            const mediaPath = `${this.config.projectHostingUrl}/${this.roomId}/${this.subroomId}/${name}`;

            console.log(`Upload of ${mediaPath} was successful`);

            delete this.roomSubUploadUrls[name];

            return mediaPath
        } catch (ex) {
            console.error(`Exception during share: ${ex}`)
        }

        return null
    }

    // https://stackoverflow.com/a/5100158/4332314
    public dataURItoBlob(dataURI: string) {
        // convert base64/URLEncoded data component to raw binary data held in a string
        let byteString;
        if (dataURI.split(',')[0].indexOf('base64') >= 0)
            byteString = atob(dataURI.split(',')[1]);
        else
            byteString = unescape(dataURI.split(',')[1]);

        // separate out the mime component
        const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to a typed array
        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i)
        }

        return new Blob([ia], { type: mimeString })
    }

    public static get hasMobileShare() {
        const nav: any = window.navigator;
        return !!(nav && nav.share)
    }

    private getRoomUrls(baseUrl: string, uploadKey: string, uploadNames: string[]) {
        const urls: { [name: string]: string } = {};

        // Everything except background (which was user-uploaded) has .png extension
        uploadNames.forEach(name => urls[name] = `${baseUrl}/${uploadKey}/${name === "background" ? name : `${name}.png`}`);
        return urls
    }

    private getRoomSubUrls(baseUrl: string, uploadKey: string, subKey: string, uploadNames: string[]) {
        const urls: { [name: string]: string } = {};
        uploadNames.forEach(name => urls[name] = `${baseUrl}/${uploadKey}/${subKey}/${name}`);
        return urls
    }

    public static getImageCrop(image:HTMLImageElement | HTMLCanvasElement, width:number, height:number) {
        const imageAspectRatio = image.width / image.height;
        const canvasAspectRatio = width / height;

        let cropW = image.width, cropH = image.height, cropX = 0, cropY = 0;

        if (imageAspectRatio < canvasAspectRatio) {
            cropH = cropW / canvasAspectRatio;
            cropY = (image.height - cropH) / 2 //centered
        } else {
            cropW = cropH * canvasAspectRatio;
            cropX = (image.width - cropW) / 2 //centered horizontally
        }

        return {x:cropX, y:cropY, width:cropW, height:cropH}
    }

    public static getResizedImage(image:HTMLImageElement, width:number, height:number) {

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const crop = this.getImageCrop(image, width, height);

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(image,
            crop.x, crop.y, crop.width, crop.height,
            0, 0, canvas.width, canvas.height);

        return canvas.toDataURL("image/jpeg")
    }
}